import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook para garantir que chamadas sejam finalizadas corretamente no banco de dados.
 * Usado quando o modal fecha, o usuário sai da página, ou o app reinicia.
 */
export function useCallFinalization() {
  /**
   * Verifica se uma chamada ainda está pendente e a finaliza se necessário.
   * Calcula duração automaticamente se não foi definida.
   */
  const ensureCallFinalized = useCallback(async (callRecordId: string | null) => {
    if (!callRecordId) return;

    try {
      // Busca status atual da chamada
      const { data: call, error: fetchError } = await supabase
        .from('calls')
        .select('status, started_at, duration_seconds, ended_at')
        .eq('id', callRecordId)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching call for finalization:', fetchError);
        return;
      }

      if (!call) {
        console.warn(`Call ${callRecordId} not found for finalization`);
        return;
      }

      // Se já está finalizada, não faz nada
      const finalStatuses = ['completed', 'failed', 'no-answer', 'canceled', 'busy'];
      if (finalStatuses.includes(call.status || '')) {
        console.log(`✅ Call ${callRecordId} already finalized with status: ${call.status}`);
        return;
      }

      // Chamada ainda está pendente - finalizar
      const endedAt = call.ended_at || new Date().toISOString();
      let durationSeconds = call.duration_seconds;

      // Calcula duração se não tiver e tiver started_at
      if (!durationSeconds && call.started_at) {
        const startTime = new Date(call.started_at).getTime();
        const endTime = call.ended_at ? new Date(call.ended_at).getTime() : Date.now();
        durationSeconds = Math.max(0, Math.floor((endTime - startTime) / 1000));
      }

      // Determina status final baseado nos dados disponíveis
      let finalStatus: string;
      if (call.status === 'initiating') {
        finalStatus = 'failed';
      } else if (durationSeconds && durationSeconds > 0) {
        finalStatus = 'completed';
      } else if (call.started_at) {
        // Teve started_at mas duração zero - pode ser no-answer ou failed
        finalStatus = 'no-answer';
      } else {
        finalStatus = 'failed';
      }

      console.log(`🔧 Finalizing call ${callRecordId}: ${call.status} → ${finalStatus} (duration: ${durationSeconds || 0}s)`);

      const { error: updateError } = await supabase
        .from('calls')
        .update({
          status: finalStatus,
          ended_at: endedAt,
          duration_seconds: durationSeconds || 0,
        })
        .eq('id', callRecordId);

      if (updateError) {
        console.error('Error finalizing call:', updateError);
      } else {
        console.log(`✅ Call ${callRecordId} finalized as ${finalStatus}`);

        // Sync: update matching calendar_event to reflect the final status
        if (finalStatus === 'completed' || finalStatus === 'no-answer' || finalStatus === 'failed') {
          try {
            // Fetch call details needed for matching
            const { data: fullCall } = await supabase
              .from('calls')
              .select('title, scheduled_for, contact_id')
              .eq('id', callRecordId)
              .maybeSingle();

            if (fullCall?.title && fullCall?.scheduled_for) {
              const ceStatus = finalStatus === 'completed' ? 'completed' : 'canceled';
              let ceQuery = (supabase as any)
                .from('calendar_events')
                .update({ status: ceStatus })
                .eq('title', fullCall.title)
                .eq('start_at', fullCall.scheduled_for)
                .eq('status', 'scheduled');

              if (fullCall.contact_id) {
                ceQuery = ceQuery.eq('contact_id', fullCall.contact_id);
              }

              const { error: ceError } = await ceQuery;
              if (ceError) {
                console.error('Error syncing calendar_event after call finalization:', ceError);
              } else {
                console.log(`✅ Matching calendar_event updated to ${ceStatus}`);
              }
            }
          } catch (ceErr) {
            console.error('Error syncing calendar_event:', ceErr);
          }
        }
      }
    } catch (err) {
      console.error('Error in ensureCallFinalized:', err);
    }
  }, []);

  /**
   * Limpa chamadas antigas do usuário que ficaram pendentes.
   * Chamado na inicialização do app.
   */
  const cleanupStaleCalls = useCallback(async (userId: string) => {
    try {
      // Busca chamadas pendentes do usuário criadas há mais de 5 minutos
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
      
      const { data: staleCalls, error: fetchError } = await supabase
        .from('calls')
        .select('id, status, started_at, duration_seconds')
        .eq('created_by', userId)
        .in('status', ['in-progress', 'ringing', 'initiating'])
        .lt('created_at', fiveMinutesAgo);

      if (fetchError) {
        console.error('Error fetching stale calls:', fetchError);
        return;
      }

      if (!staleCalls || staleCalls.length === 0) {
        return;
      }

      console.log(`🧹 Found ${staleCalls.length} stale calls to cleanup`);

      for (const staleCall of staleCalls) {
        await ensureCallFinalized(staleCall.id);
      }
    } catch (err) {
      console.error('Error cleaning up stale calls:', err);
    }
  }, [ensureCallFinalized]);

  return {
    ensureCallFinalized,
    cleanupStaleCalls,
  };
}
